import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 999999;
	padding: 20px;
`;

const ModalContainer = styled.div`
	background: white;
	border-radius: 12px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	width: 500px;
	max-width: 90vw;
	position: relative;
	padding: 32px;
`;

const CloseButton = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	background: none;
	border: none;
	font-size: 24px;
	color: #6b7280;
	cursor: pointer;
	padding: 4px;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	&:hover {
		color: #374151;
	}
`;

const ModalTitle = styled.h3`
	font-size: 18px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 24px;
`;

const Label = styled.label`
	display: block;
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	margin-bottom: 8px;
`;

const Required = styled.span`
	color: #dc2626;
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
	width: 100%;
	padding: 12px 16px;
	border: 1px solid ${props => props.hasError ? '#dc2626' : '#d1d5db'};
	border-radius: 6px;
	font-size: 14px;
	min-height: 100px;
	resize: vertical;
	font-family: inherit;
	&:focus {
		outline: none;
		border-color: ${props => props.hasError ? '#dc2626' : '#2563EB'};
	}
`;

const ErrorMessage = styled.div`
	color: #dc2626;
	font-size: 12px;
	margin-top: 4px;
`;

const ButtonGroup = styled.div`
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 24px;
`;

const Button = styled.button<{ variant: 'cancel' | 'confirm' }>`
	padding: 10px 20px;
	border-radius: 6px;
	border: none;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
	
	${props => props.variant === 'cancel' ? `
		background-color: #F3F4F6;
		color: #374151;
		&:hover {
			background-color: #E5E7EB;
		}
	` : `
		background-color: #EF4444;
		color: white;
		&:hover {
			background-color: #DC2626;
		}
	`}
`;

interface RejectBookingModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (reason: string) => void;
}

export default function RejectBookingModal({ isOpen, onClose, onConfirm }: RejectBookingModalProps) {
	const [reason, setReason] = useState('');
	const [error, setError] = useState('');

	useEffect(() => {
		if (!isOpen) {
			setReason('');
			setError('');
		}
	}, [isOpen]);

	const handleConfirm = () => {
		if (!reason.trim()) {
			setError('Vui lòng nhập lý do từ chối');
			return;
		}
		onConfirm(reason.trim());
	};

	if (!isOpen) return null;

	const modalContent = (
		<ModalOverlay onClick={onClose}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<CloseButton onClick={onClose}>×</CloseButton>
				<ModalTitle>Từ chối booking</ModalTitle>
				
				<Label>
					Lý do từ chối <Required>*</Required>
				</Label>
				<TextArea
					value={reason}
					onChange={(e) => {
						setReason(e.target.value);
						if (error) setError('');
					}}
					placeholder="Nhập lý do từ chối..."
					hasError={!!error}
				/>
				{error && <ErrorMessage>{error}</ErrorMessage>}
				
				<ButtonGroup>
					<Button variant="cancel" onClick={onClose}>
						Hủy
					</Button>
					<Button variant="confirm" onClick={handleConfirm}>
						Từ chối
					</Button>
				</ButtonGroup>
			</ModalContainer>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}

